import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setMessage } from '../message';
import SoalService from '../../services/soalServices';
import { clearAuth } from 'store/auth';

const onProgress = (thunkAPI) => (value) => {
  thunkAPI.dispatch(setProgres(value));
};

export const getList = createAsyncThunk('soal/list', async ({ latihanID, page, perPage, sort, orderBy, search }, thunkAPI) => {
  try {
    const response = await SoalService.getList(latihanID, page, perPage, sort, orderBy, search);

    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const createData = createAsyncThunk('soal/create', async ({ latihanID, data }, thunkAPI) => {
  try {
    const response = await SoalService.create(latihanID, data, onProgress(thunkAPI));
    return response.data;
  } catch (error) {
    if (error?.response?.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }

    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(message);
  }
});

export const readData = createAsyncThunk('soal/read', async ({ latihanID, id }, thunkAPI) => {
  try {
    const response = await SoalService.read(latihanID, id);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const updateData = createAsyncThunk('soal/update', async ({ latihanID, id, data }, thunkAPI) => {
  try {
    const response = await SoalService.update(latihanID, id, data, onProgress(thunkAPI));
    return response.data;
  } catch (error) {
    if (error?.response?.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteData = createAsyncThunk('soal/delete', async ({ latihanID, id }, thunkAPI) => {
  try {
    const response = await SoalService.del(latihanID, id);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const getSession = createAsyncThunk('soal/getSession', async ({ latihanID, sessionID }, thunkAPI) => {
  try {
    const response = await SoalService.getSession(latihanID, sessionID);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const preview = createAsyncThunk('soal/preview', async (latihanID, thunkAPI) => {
  try {
    const response = await SoalService.preview(latihanID);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const storeScore = createAsyncThunk('soal/storeScore', async (data, thunkAPI) => {
  try {
    const response = await SoalService.storeScore(data);
    const message = (response && response.data && response.data.message) || response.message || response.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'success' }));
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const changeStatus = createAsyncThunk('soal/changeStatus', async ({ latihanID, soalID, data }, thunkAPI) => {
  try {
    const response = await SoalService.changeStatus(latihanID, soalID, data);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

const initialState = {
  isLoading: false,
  isStoreLoading: false,
  list: [],
  data: {},
  pilihan: [],
  soal: [],
  session: null,
  latihan: null,
  error: null,
  progress: 0
};

const soalSlice = createSlice({
  name: 'soal',
  initialState,
  reducers: {
    setProgres: (state, action) => {
      return {
        ...state,
        progress: action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getList.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    }),
      builder.addCase(getList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      }),
      builder.addCase(getList.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(createData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(createData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.progress = 0;
      }),
      builder.addCase(createData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      }),
      builder.addCase(readData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(readData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      }),
      builder.addCase(readData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(updateData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.progress = 0;
      }),
      builder.addCase(updateData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      }),
      builder.addCase(deleteData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(deleteData.fulfilled, (state) => {
        state.isLoading = false;
        state.data = {};
      }),
      builder.addCase(deleteData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(getSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.latihan = null;
        state.soal = null;
        state.session = null;
      }),
      builder.addCase(getSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.latihan = action.payload.latihan;
        state.soal = action.payload.soal;
        state.session = action.payload.session;
      }),
      builder.addCase(getSession.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(preview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(preview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.latihan = action.payload.latihan;
        state.soal = action.payload.soal;
      }),
      builder.addCase(preview.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(storeScore.pending, (state) => {
        state.isStoreLoading = true;
        state.error = null;
      }),
      builder.addCase(storeScore.fulfilled, (state) => {
        state.isStoreLoading = false;
      }),
      builder.addCase(storeScore.rejected, (state) => {
        state.isStoreLoading = false;
      }),
      builder.addCase(changeStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(changeStatus.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(changeStatus.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

const { reducer, actions } = soalSlice;
export const { setProgres } = actions;
export default reducer;
